import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FileText, Newspaper, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface Post {
  id: string;
  title: string;
  excerpt: string | null;
  slug: string;
  published_at: string | null;
  category: string | null;
  featured_image_url: string | null;
}

interface PostsScrollerProps {
  title: string;
  description: string;
  posts: Post[];
  type: 'insights' | 'blog';
  loading?: boolean;
}

export const PostsScroller = ({ title, description, posts, type, loading }: PostsScrollerProps) => {
  const navigate = useNavigate();
  const Icon = type === 'insights' ? Newspaper : FileText;
  const basePath = type === 'insights' ? '/insights' : '/blog';

  const handlePostClick = (slug: string) => {
    navigate(`${basePath}/${slug}`);
  };

  const handleViewAll = () => {
    navigate(basePath);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <Card className="shadow-lg border-0">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Icon className="h-5 w-5" />
            {title}
          </CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
        <Button variant="ghost" size="sm" onClick={handleViewAll} className="flex items-center gap-1">
          View All <ArrowRight className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No {type} posts available
          </div>
        ) : (
          <ScrollArea className="w-full whitespace-nowrap">
            <div className="flex gap-4 pb-4">
              {posts.map((post) => (
                <div
                  key={post.id}
                  className="w-[280px] flex-shrink-0 cursor-pointer group"
                  onClick={() => handlePostClick(post.slug)}
                >
                  <Card className="h-full hover:shadow-md transition-shadow border hover:border-primary/30">
                    {post.featured_image_url && (
                      <div className="h-32 overflow-hidden rounded-t-lg">
                        <img
                          src={post.featured_image_url}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <CardContent className={`p-4 ${!post.featured_image_url ? 'pt-4' : ''}`}>
                      <div className="flex items-center gap-2 mb-2">
                        {post.category && (
                          <Badge variant="secondary" className="text-xs">
                            {post.category}
                          </Badge>
                        )}
                        {post.published_at && (
                          <span className="text-xs text-muted-foreground">
                            {formatDate(post.published_at)}
                          </span>
                        )}
                      </div>
                      <h3 className="font-semibold text-sm line-clamp-2 whitespace-normal group-hover:text-primary transition-colors">
                        {post.title}
                      </h3>
                      {post.excerpt && (
                        <p className="text-xs text-muted-foreground mt-2 line-clamp-2 whitespace-normal">
                          {post.excerpt}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};
